import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';
import {take} from 'rxjs/operators';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {AdminPanelState} from '@features/admin-panel/store/reducers/admin-panel.reducer';
import {
  AddSolutionAction,
  CloneUserAction,
  CreateSupportTicketForUserAction,
  SendPasswordAction
} from '@features/admin-panel/store/actions/admin-panel.action';
import {
  AddSolutionCommand,
  CloneUserCommand,
  CreateSupportTicketForUserCommand,
  SendPasswordCommand
} from '@features/admin-panel/commands/admin-panel.command';
import {AddSolutionComponent} from '@features/admin-panel/components/add-solution/add-solution.component';
import {CloneUserComponent} from '@features/admin-panel/components/clone-user/clone-user.component';
import {SendPasswordComponent} from '@features/admin-panel/components/send-password/send-password.component';
import {CreateSupportTicketComponent} from '@features/admin-panel/components/create-support-ticket/create-support-ticket.component';

@Injectable({
  providedIn: 'root',
})
export class AdminPanelUserDetailHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTabModel: TabModel;
  magicLinkModelValueChanged: EventEmitter<any> = new EventEmitter<any>();

  // Set by the form component when user data is loaded
  currentUser: any = null;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private commandService: CommandService,
    private snackBarService: SnackBarService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private store: Store<AdminPanelState>
  ) {}

  destroy(): void {
    this.invalidateSubject?.close();
  }

  onBeforeLoad(): void {}

  executeCommand(command: PlatformCommand) {
    switch (command.name) {
      case 'AddSolutionPlatformCommand':
        this.addSolution(command);
        break;
      case 'CloneUserPlatformCommand':
        this.cloneUser(command);
        break;
      case 'SendPasswordPlatformCommand':
        this.sendPassword(command);
        break;
      case 'CreateSupportTicketPlatformCommand':
        this.createSupportTicket(command);
        break;
      default:
        this.executeCommandToApi(command);
    }
  }

  private getUserName(command: PlatformCommand): string {
    return command.fieldValues?.['UserName']
      ?? this.magicLinkModel?.tabBar?.tabs
        ?.find(t => t.tabData?.['UserName'])
        ?.tabData?.['UserName'];
  }

  private addSolution(command: PlatformCommand) {
    const userName = this.getUserName(command);
    const dialogRef = this.dialog.open(AddSolutionComponent, {
      width: '600px',
      disableClose: true,
      data: {userName}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.save) {
        const legacyCommand = new AddSolutionCommand(result.solutions);
        this.store.dispatch(new AddSolutionAction(legacyCommand));
      }
    });
  }

  private cloneUser(command: PlatformCommand) {
    const user = this.currentUser;
    if (!user) return;
    const dialogRef = this.dialog.open(CloneUserComponent, {
      width: '600px',
      disableClose: true,
      data: {user, companyId: user.companyId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.save) {
        const legacyCommand = new CloneUserCommand(result.user, result.email, result.companyId);
        this.store.dispatch(new CloneUserAction(legacyCommand));
      }
    });
  }

  private sendPassword(command: PlatformCommand) {
    const user = this.currentUser;
    if (!user) return;
    const dialogRef = this.dialog.open(SendPasswordComponent, {
      width: '600px',
      disableClose: true,
      data: {user}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.save) {
        const legacyCommand = new SendPasswordCommand(
          user.userName,
          result.sendPassword.generatePassword,
          result.sendPassword.password,
          result.sendPassword.passwordChange,
          result.sendPassword.sendCopyToMe
        );
        this.store.dispatch(new SendPasswordAction(legacyCommand));
      }
    });
  }

  private createSupportTicket(command: PlatformCommand) {
    const user = this.currentUser;
    if (!user) return;
    const dialogRef = this.dialog.open(CreateSupportTicketComponent, {
      width: '600px',
      disableClose: true,
      data: {userCompanyId: user.companyId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.save) {
        const legacyCommand = new CreateSupportTicketForUserCommand(
          result.supportTicket.title,
          result.supportTicket.description,
          [],
          user.email,
          user.companyId,
          user.firstName,
          user.lastName,
          user.email,
          ''
        );
        this.store.dispatch(new CreateSupportTicketForUserAction(legacyCommand));
      }
    });
  }

  executeCommandToApi(command: PlatformCommand) {
    this.fuseSplashScreenService.show();
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(
      commandResponse => {
        this.fuseSplashScreenService.hide();
        if (commandResponse.fieldValues['SnackBarMessage']) {
          this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage']);
        }
        if (commandResponse.fieldValues['RedirectUrl']) {
          this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
          return;
        }
        this.invalidateSubject?.invalidate();
      },
      error => {
        this.fuseSplashScreenService.hide();
        console.error('Failed ' + command.name, error);
      }
    );
  }

  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
  }

  valueChanged($event: any) {}
}
